// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    markSkipOnCompleteIfOnErrorIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import {
    getInventoryForBundleProduct,
    getInventoryForSingleProduct,
    RealTimeInventoryModel,
    RealTimeInventoryParameter,
} from "@stoneage/App/Services/CustomRealTimeInventory/CustomRealTimeInventoryService";

export interface CustomRealTimeInventoryParameter {
    productNumber: string;
    isSingleProduct: boolean;
    components: [];
    onSuccess?: (results: any) => void;
    onError?: (error: string) => void;
}

type HandlerType = ApiHandlerDiscreteParameter<
    CustomRealTimeInventoryParameter,
    RealTimeInventoryParameter,
    RealTimeInventoryModel,
    ServiceResult<RealTimeInventoryModel>
>;

export const PopulateApiParameter: HandlerType = props => {
    if (props.parameter.isSingleProduct) {
        props.apiParameter = {
            inventory: {
                productNumber: props.parameter.productNumber,
            } as RealTimeInventoryModel,
        };
    } else {
        props.apiParameter = {
            inventory: {
                components: props.parameter.components,
                bundleErpNumber: props.parameter.productNumber,
            } as RealTimeInventoryModel,
        };
    }
};

export const RequestInventory: HandlerType = async props => {
    if (props.parameter.isSingleProduct) {
        props.apiResult = await getInventoryForSingleProduct(props.apiParameter);
    } else {
        props.apiResult = await getInventoryForBundleProduct(props.apiParameter);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult?.result);
    }
};

export const chain = [PopulateApiParameter, RequestInventory, ExecuteOnErrorCallback, ExecuteOnSuccessCallback];

const getCustomRealTimeWarehouseInventory = createHandlerChainRunner(chain, "GetCustomRealTimeWarehouseInventory");
export default getCustomRealTimeWarehouseInventory;
